import type { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { http } from '@/shared/api/httpClient';
import { unwrapEnvelopeData, type ApiEnvelope } from '@/shared/api/apiEnvelope';

import type {
  OrderCancelReturnAction,
  OrderRow,
  OrderStats,
  OrderSummary,
  OrderSummaryParams,
  OrdersListParams,
  UpdateAdminOrderStatusData,
  UpdateAdminOrderStatusRequest,
} from '../types';
import { ordersKeys } from './keys';
import { mapRemoteOrderToDetail } from '../lib/mapRemoteOrderToDetail';
import {
  parsePendingPaymentResolution,
  readPendingPaymentTracking,
} from '../lib/pendingPaymentContinuation';
import { normalizeOrderStatus } from '../lib/orderStatusPresentation';
import { rupeesToPaise } from '../lib/mapRemoteOrderToDetail';
import type { PendingPaymentResolution } from '../types';

export interface RemoteOrderItem {
  binLocations: unknown[];
  id: string;
  productId: string;
  productName: string;
  productImageUrl: string;
  quantity: number;
  unitPrice: number;
  mrp: number;
  totalPrice: number;
  pickedQuantity: number;
  status: string;
  kitId: string;
}

export interface RemoteOrder {
  items: RemoteOrderItem[];
  id: string;
  orderNumber: string;
  userId: string;
  storeId: string;
  status: string;
  subtotal: number;
  deliveryFee: number;
  handlingFee: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  deliveryAddress: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    pincode: string;
    latitude: number;
    longitude: number;
    label: string;
    phoneNumber?: string;
    customerName?: string;
  };
  promisedDeliveryMinutes: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
  confirmedAt: string;
  deliveredAt: string;
  cancelledAt: string;
  cancellationReason: string;
  returnedAt?: string;
  returnReason?: string;
  assignedDeliveryAgentName: string;
  slaBreached: boolean;
  slaPickingStartedAt: string;
  storeName: string;
  /** Wire value e.g. PREPAID, COD */
  paymentMethod?: string;
  paymentStatus?: string;
  collectionMode?: string;
  collectionStatus?: string;
  /** String or number integer paise */
  collectedAmountPaisa?: string | number;
  customerName?: string;
  customerPhone?: string;
  statusHistory?: Array<{
    fromStatus?: string;
    toStatus?: string;
    notes?: string;
    changedBy?: string;
    createdAt?: string;
  }>;
  pendingPaymentResolution?: string;
  pendingPaymentContinuedBy?: string;
  pendingPaymentContinuedAt?: string;
}

export interface RemoteOrdersListData {
  items: RemoteOrder[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type { OrdersListParams };

export interface OrderListQueryResult {
  items: OrderRow[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

function mapRemoteOrderToRow(order: RemoteOrder): OrderRow {
  const items = Array.isArray(order.items) ? order.items : [];
  const totalItems = items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
  const pendingPayment = readPendingPaymentTracking(order);
  const addr = order.deliveryAddress;
  const customerName =
    (typeof addr?.customerName === 'string' && addr.customerName.trim()) ||
    (typeof addr?.label === 'string' && addr.label.trim()) ||
    order.userId ||
    'Customer';

  return {
    id: order.id,
    orderNumber: order.orderNumber,
    customerName,
    status: normalizeOrderStatus(order.status),
    totalItems,
    totalPaise: rupeesToPaise(order.totalAmount),
    originId: order.storeId,
    originName: order.storeName,
    deliveredAtIso: typeof order.deliveredAt === 'string' ? order.deliveredAt : undefined,
    paymentMethod: typeof order.paymentMethod === 'string' ? order.paymentMethod : undefined,
    collectionMode: typeof order.collectionMode === 'string' ? order.collectionMode : undefined,
    pendingPaymentResolution: pendingPayment.resolution,
    pendingPaymentContinuedBy: pendingPayment.continuedBy,
    pendingPaymentContinuedAt: pendingPayment.continuedAtIso,
  };
}

function buildOrdersListHttpParams(params: OrdersListParams): Record<string, string | number> {
  const page = params.page ?? 1;
  const limit = params.limit ?? 20;
  const out: Record<string, string | number> = { page, limit };

  if (params.storeId) out.storeId = params.storeId;
  if (params.status?.trim()) out.status = params.status.trim();
  if (params.orderNumber?.trim()) out.orderNumber = params.orderNumber.trim();
  if (params.sortBy) out.sortBy = params.sortBy;
  if (params.sortOrder) out.sortOrder = params.sortOrder;

  return out;
}

export async function fetchOrderList(params: OrdersListParams = {}): Promise<OrderListQueryResult> {
  const httpParams = buildOrdersListHttpParams(params);
  const response: AxiosResponse<unknown> = await http.get('/admin/orders', {
    params: httpParams,
  });

  const data = unwrapEnvelopeData<RemoteOrdersListData>(response.data, response.status);
  const rawItems = Array.isArray(data.items) ? data.items : [];
  const items = rawItems.map(mapRemoteOrderToRow);
  const limit =
    typeof data.limit === 'number' && data.limit > 0
      ? data.limit
      : (params.limit ?? 20);
  const total = typeof data.total === 'number' && Number.isFinite(data.total) ? data.total : items.length;
  const pageCountRaw = data.totalPages;
  const pageCount =
    typeof pageCountRaw === 'number' && pageCountRaw > 0
      ? pageCountRaw
      : Math.max(1, Math.ceil(total / Math.max(limit, 1)));

  return {
    items,
    total,
    page: typeof data.page === 'number' && data.page > 0 ? data.page : (params.page ?? 1),
    limit,
    pageCount,
  };
}

function numField(o: Record<string, unknown>, camel: string, snake: string, fallback = 0): number {
  const raw = o[camel] ?? o[snake];
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.length > 0) {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function strFieldLocal(
  o: Record<string, unknown>,
  camel: string,
  snake: string,
  fallback = '',
): string {
  const raw = o[camel] ?? o[snake];
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return fallback;
}

export function mapOrderSummary(raw: unknown): OrderSummary {
  if (!raw || typeof raw !== 'object') {
    return {
      storeId: '',
      dateFrom: '',
      dateTo: '',
      totalOrders: 0,
      totalOrdersChangePercent: 0,
      totalOrdersComparisonLabel: '',
      pendingFulfillment: 0,
      outForDelivery: 0,
      delayed: 0,
      delayedChange: 0,
    };
  }
  const o = raw as Record<string, unknown>;
  return {
    storeId: strFieldLocal(o, 'storeId', 'store_id'),
    dateFrom: strFieldLocal(o, 'dateFrom', 'date_from'),
    dateTo: strFieldLocal(o, 'dateTo', 'date_to'),
    totalOrders: numField(o, 'totalOrders', 'total_orders'),
    totalOrdersChangePercent: numField(
      o,
      'totalOrdersChangePercent',
      'total_orders_change_percent',
    ),
    totalOrdersComparisonLabel: strFieldLocal(
      o,
      'totalOrdersComparisonLabel',
      'total_orders_comparison_label',
    ),
    pendingFulfillment: numField(o, 'pendingFulfillment', 'pending_fulfillment'),
    outForDelivery: numField(o, 'outForDelivery', 'out_for_delivery'),
    delayed: numField(o, 'delayed', 'delayed'),
    delayedChange: numField(o, 'delayedChange', 'delayed_change'),
  };
}

export function orderSummaryToStats(summary: OrderSummary): OrderStats {
  return {
    totalOrders: summary.totalOrders,
    totalDeltaPercent: summary.totalOrdersChangePercent,
    totalComparisonLabel: summary.totalOrdersComparisonLabel,
    pendingFulfillment: summary.pendingFulfillment,
    outForDelivery: summary.outForDelivery,
    delayed: summary.delayed,
    delayedDelta: summary.delayedChange,
  };
}

/**
 * Dashboard KPI aggregates — `GET /admin/orders/summary`.
 * Store filter only for v1 (API defaults date window to current IST month).
 */
export async function fetchOrderSummary(params: OrderSummaryParams = {}): Promise<OrderSummary> {
  const response: AxiosResponse<unknown> = await http.get('/admin/orders/summary', {
    params: {
      ...(params.storeId ? { storeId: params.storeId } : {}),
    },
  });
  const unwrapped = unwrapEnvelopeData<unknown>(response.data, response.status);
  return mapOrderSummary(unwrapped);
}

export function useOrderSummaryQuery(params: OrderSummaryParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ordersKeys.summary({ storeId: params.storeId }),
    queryFn: () => fetchOrderSummary(params),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
    retry: 1,
  });
}

export async function fetchOrderDetail(idOrNumber: string) {
  const base = '/admin/orders'.replace(/\/$/, '');
  const safeId = encodeURIComponent(idOrNumber);
  const res = await http.get<unknown>(`${base}/${safeId}`);
  const unwrapped = unwrapEnvelopeData<RemoteOrder>(res.data, res.status);
  const dtoRaw =
    unwrapped ??
    (res.data as { data?: unknown })?.data ??
    (res.data as { items?: unknown[] })?.items?.[0];

  if (!dtoRaw || typeof dtoRaw !== 'object') throw new Error('Order not found');

  const dto = dtoRaw as Partial<RemoteOrder> & {
    items?: unknown[];
    deliveryAddress?: Partial<RemoteOrder['deliveryAddress']>;
    assignedDeliveryAgent?: string;
    paymentMethod?: string;
    customerName?: string;
    customerPhone?: string;
    paymentStatus?: string;
    collectionMode?: string;
    collectionStatus?: string;
    collectedAmountPaisa?: string | number;
    returnedAt?: string;
    returnReason?: string;
    statusHistory?: unknown;
  };

  return mapRemoteOrderToDetail(dto, idOrNumber);
}

export function useOrderDetailQuery(idOrNumber: string | undefined) {
  return useQuery({
    queryKey: ordersKeys.detail(idOrNumber ?? ''),
    queryFn: () => fetchOrderDetail(idOrNumber as string),
    enabled: Boolean(idOrNumber),
    staleTime: 30_000,
    retry: 1,
  });
}

export interface OrderPayoutDetails {
  accountHolderName: string;
  accountNumber: string;
  ifscCode: string;
  upiId: string;
}

export interface CancelOrReturnOrderInput {
  orderId: string;
  action: OrderCancelReturnAction;
  reason: string;
  payout?: OrderPayoutDetails;
}

export interface CancelOrReturnOrderResult {
  success: boolean;
  message: string;
  approvalRequired: boolean;
}

function strFromUnknown(value: unknown): string {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : '';
}

function mapCancelOrReturnResult(body: unknown, data: unknown): CancelOrReturnOrderResult {
  const envelope = body && typeof body === 'object' ? (body as ApiEnvelope<unknown>) : {};
  const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};

  const message =
    strFromUnknown(payload.message) ||
    strFromUnknown(envelope.message) ||
    '';

  const approvalRequired =
    payload.approval_required === true ||
    payload.approvalRequired === true ||
    (envelope as { approval_required?: unknown }).approval_required === true ||
    (envelope as { approvalRequired?: unknown }).approvalRequired === true;

  return {
    success: payload.success !== false,
    message,
    approvalRequired,
  };
}

export async function cancelOrReturnOrder(
  input: CancelOrReturnOrderInput,
): Promise<CancelOrReturnOrderResult> {
  const body: Record<string, unknown> = {
    action: input.action,
    reason: input.reason,
  };
  if (input.payout) body.payout = input.payout;

  const res = await http.post<unknown>(
    `/admin/orders/${encodeURIComponent(input.orderId)}/cancel-or-return`,
    body,
    { idempotency: true },
  );

  const unwrapped = unwrapEnvelopeData<unknown>(res.data, res.status);
  return mapCancelOrReturnResult(res.data, unwrapped);
}

export function useCancelOrReturnOrderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...ordersKeys.all, 'cancel-or-return'] as const,
    mutationFn: cancelOrReturnOrder,
    onSettled: async (_data, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ordersKeys.all }),
        queryClient.invalidateQueries({ queryKey: ordersKeys.detail(variables.orderId) }),
      ]);
    },
  });
}

function mapUpdateOrderStatusData(data: unknown): UpdateAdminOrderStatusData {
  const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return {
    success: payload.success !== false,
    message: strFromUnknown(payload.message),
  };
}

/** Gateway re-checks the live payment, so this POST can take several seconds. */
const CONTINUE_PENDING_PAYMENT_TIMEOUT_MS = 45_000;

export interface ContinuePendingPaymentInput {
  /** Order UUID. Not the order number. */
  orderId: string;
}

export interface ContinuePendingPaymentResult {
  orderId: string;
  orderNumber: string;
  status: string;
  paymentMethod: string;
  paymentStatus: string;
  resolution: PendingPaymentResolution | '';
  pendingPaymentContinuedBy: string;
  pendingPaymentContinuedAt: string;
  message: string;
}

function mapContinuePendingPaymentResult(data: unknown): ContinuePendingPaymentResult {
  const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const tracking = readPendingPaymentTracking({
    pendingPaymentResolution: payload.resolution ?? payload.pendingPaymentResolution,
    pendingPaymentContinuedBy: payload.pendingPaymentContinuedBy,
    pendingPaymentContinuedAt: payload.pendingPaymentContinuedAt,
  });
  const resolution = parsePendingPaymentResolution(
    strFromUnknown(payload.resolution) || tracking.resolution,
  );

  return {
    orderId: strFromUnknown(payload.orderId),
    orderNumber: strFromUnknown(payload.orderNumber),
    status: strFromUnknown(payload.status),
    paymentMethod: strFromUnknown(payload.paymentMethod),
    paymentStatus: strFromUnknown(payload.paymentStatus),
    resolution,
    pendingPaymentContinuedBy:
      strFromUnknown(payload.pendingPaymentContinuedBy) || tracking.continuedBy,
    pendingPaymentContinuedAt:
      strFromUnknown(payload.pendingPaymentContinuedAt) || tracking.continuedAtIso,
    message: strFromUnknown(payload.message),
  };
}

export async function updateAdminOrderStatus(
  orderId: string,
  body: UpdateAdminOrderStatusRequest,
): Promise<UpdateAdminOrderStatusData> {
  const res = await http.patch<unknown>(
    `/admin/orders/${encodeURIComponent(orderId)}/status`,
    body,
  );
  const unwrapped = unwrapEnvelopeData<unknown>(res.data, res.status);
  return mapUpdateOrderStatusData(unwrapped);
}

export interface UpdateAdminOrderStatusMutationInput {
  orderId: string;
  body: UpdateAdminOrderStatusRequest;
}

export function useUpdateAdminOrderStatusMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...ordersKeys.all, 'update-status'] as const,
    mutationFn: ({ orderId, body }: UpdateAdminOrderStatusMutationInput) =>
      updateAdminOrderStatus(orderId, body),
    onSettled: async (_data, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ordersKeys.all }),
        queryClient.invalidateQueries({ queryKey: ordersKeys.detail(variables.orderId) }),
      ]);
    },
  });
}

/**
 * Re-check a pending online payment and confirm the order.
 * No request body. Auth is the same bearer header as other `/admin/orders` calls.
 * The server either confirms prepaid (`ONLINE_PAYMENT_CAPTURED`) or cash on delivery
 * (`SWITCHED_TO_COD`). Callers must branch on `resolution` and must not treat errors as a COD switch.
 */
export async function continuePendingPayment(
  input: ContinuePendingPaymentInput,
): Promise<ContinuePendingPaymentResult> {
  const res = await http.post<unknown>(
    `/admin/orders/${encodeURIComponent(input.orderId)}/continue-with-cod`,
    undefined,
    { idempotency: true, timeout: CONTINUE_PENDING_PAYMENT_TIMEOUT_MS },
  );
  const unwrapped = unwrapEnvelopeData<unknown>(res.data, res.status);
  return mapContinuePendingPaymentResult(unwrapped);
}

export function useContinuePendingPaymentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...ordersKeys.all, 'continue-with-cod'] as const,
    mutationFn: continuePendingPayment,
    onSettled: async (_data, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ordersKeys.all }),
        queryClient.invalidateQueries({ queryKey: ordersKeys.detail(variables.orderId) }),
      ]);
    },
  });
}
